'use client';

import Image from 'next/image';
import { useRouter } from 'next/navigation';
import { useCart } from './cart-provider';
import type { PublicProduct } from '@sapling/contracts/products';
import { resolveProductImage } from '@/lib/product-images';
import { formatPrice } from '@sapling/formatting/currency';
import { Button } from '@sapling/ui/components/ui/button';
import { Badge } from '@sapling/ui/components/ui/badge';
import {
  Card,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@sapling/ui/components/ui/card';
import { PlusIcon, CheckIcon } from '@heroicons/react/24/outline';

type ProductCardProps = {
  product: PublicProduct;
};

export function ProductCard({ product }: ProductCardProps) {
  const router = useRouter();
  const { addItem, items, openCart } = useCart();

  const cartItem = items.find((item) => item.sku === product.sku);
  const isInCart = Boolean(cartItem);
  const canAddMore = true;

  const image = product.imageKeys[0]
    ? resolveProductImage(product.imageKeys[0])
    : undefined;

  const handleAddToCart = async () => {
    if (canAddMore) {
      await addItem({
        id: product.sku,
        sku: product.sku,
        name: product.name,
        price: product.price,
        image,
      });
      openCart();
    }
  };

  const handleBuyNow = async () => {
    const cartId = await addItem({
      id: product.sku,
      sku: product.sku,
      name: product.name,
      price: product.price,
      image,
    });
    router.push(`/butik/checkout/${cartId}/review`);
  };

  const getCategoryBadgeVariant = (category: string) => {
    switch (category) {
      case 'events':
        return 'default';
      case 'publications':
        return 'secondary';
      case 'education':
        return 'outline';
      default:
        return 'secondary';
    }
  };

  return (
    <Card className="group flex h-full flex-col overflow-hidden transition-shadow hover:shadow-lg">
      {/* Product Image */}
      <div className="bg-muted relative aspect-4/3 overflow-hidden">
        {image ? (
          <Image
            src={image}
            alt={product.name}
            fill
            className="object-cover transition-transform duration-300 group-hover:scale-105"
            sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 25vw"
          />
        ) : (
          <div className="text-muted-foreground flex h-full items-center justify-center">
            <span>Ingen bild</span>
          </div>
        )}

        {/* Stock Badge */}
        {!product.inStock && (
          <Badge variant="destructive" className="absolute top-2 right-2">
            Slutsåld
          </Badge>
        )}

        {/* Category Badge */}
        <Badge
          variant={getCategoryBadgeVariant(product.category)}
          className="absolute top-2 left-2"
        >
          {product.category === 'events' && 'Evenemang'}
          {product.category === 'publications' && 'Publikation'}
          {product.category === 'education' && 'Utbildning'}
        </Badge>
      </div>

      <CardHeader className="pb-3">
        <CardTitle className="line-clamp-2 text-lg">{product.name}</CardTitle>
        <CardDescription className="line-clamp-3 text-sm">
          {product.description}
        </CardDescription>
      </CardHeader>

      <CardFooter className="flex flex-1 flex-col justify-end gap-3 pt-0">
        {/* Price */}
        <div className="flex w-full items-center justify-between">
          <div>
            <span className="text-2xl font-bold">
              {formatPrice(product.price)}
            </span>
            {product.vatRate > 0 && (
              <span className="text-muted-foreground ml-1 text-xs">
                inkl. moms
              </span>
            )}
          </div>
          {cartItem && (
            <Badge variant="outline" className="text-xs">
              {cartItem.quantity} i kundvagn
            </Badge>
          )}
        </div>

        {/* Actions */}
        <div className="flex w-full flex-col gap-2">
          <Button
            onClick={handleAddToCart}
            disabled={!product.inStock || !canAddMore}
            className="w-full"
            size="sm"
          >
            {!product.inStock ? (
              'Slutsåld'
            ) : isInCart ? (
              canAddMore ? (
                <>
                  <PlusIcon className="mr-2 h-4 w-4" />
                  Lägg till fler
                </>
              ) : (
                <>
                  <CheckIcon className="mr-2 h-4 w-4" />
                  Max antal tillagd
                </>
              )
            ) : (
              <>
                <PlusIcon className="mr-2 h-4 w-4" />
                Lägg i kundvagn
              </>
            )}
          </Button>
          {product.inStock && (
            <Button
              onClick={handleBuyNow}
              variant="secondary"
              className="w-full"
              size="sm"
            >
              Köp nu
            </Button>
          )}
        </div>
      </CardFooter>
    </Card>
  );
}
